import BotSettingLeftPanel from './BotSettingLeftPanel';
import React, { useState, useEffect, useRef } from 'react';
import { useAcumenCommand } from '../../context/AcumenCommandContext';
import { useAcumen } from '../../context/AcumenContext';
import { useTheme } from '../../context/ThemeContext';

const AcumenBotSettings = ({ onClose, activeSlotId }) => {
  const { slots, updateSlots } = useAcumenCommand();
  const { query_acumen } = useAcumen();
  const { darkMode } = useTheme();
  const [currentTab, setCurrentTab] = useState('VIBE');
  const [slotConfig, setSlotConfig] = useState({});
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  const tabs = [
    { id: 'VIBE', label: 'VIBE', color: '#3b82f6' },
    { id: 'CONTEXT', label: 'CONTEXT', color: '#10b981' },
    { id: 'HELP', label: 'HELP', color: '#f59e0b' },
    { id: 'COMPLY', label: 'COMPLY', color: '#ef4444' },
    { id: 'GENERATE', label: 'GENERATE', color: '#8b5cf6' },
    { id: 'SIGNATURE', label: 'SIGNATURE', color: '#6b7280' }
  ];

  const presetColors = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // yellow
    '#ef4444', // red
    '#8b5cf6', // purple
    '#ffffff', // white
    '#f97316', // orange
    '#ec4899'  // pink
  ];

  const cursorIcons = ['B', 'C', '+', '?', 'P', 'S'];

  const agentOptions = [
    'Acumen-10', 'Security Agent', 'Budget Agent', 'Reasoning Agent',
    'Helper Agent', 'Compliance Agent', 'Research Agent', 'Drafter Agent',
    'Legal Agent', 'Analytics Agent', 'Timeline Agent', 'Market Agent'
  ];

  const clearanceLevels = ['Unclassified', 'CUI', 'Secret', 'Top Secret'];
  const stampStyles = ['Full Seal', 'Mini Stamp'];

  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('acumen-bot-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSlotConfig(parsed[activeSlotId] || {});
      } catch (e) {
        console.error('Failed to parse saved settings:', e);
      }
    }

    // Initialize with default slot config
    if (!slotConfig.name) {
      setSlotConfig({
        name: activeSlotId,
        color: '#3b82f6',
        cursorIcon: 'B',
        tone: 50,
        depth: 50,
        speed: 50,
        confidence: 50,
        agents: [],
        signature: {
          officerName: '',
          agency: '',
          title: '',
          idNumber: '',
          clearanceLevel: 'Unclassified',
          stampStyle: 'Full Seal',
          notesTemplate: ''
        }
      });
    }
  }, [activeSlotId]);

  const handleSave = () => {
    const updatedSlots = { ...slots };
    updatedSlots[activeSlotId] = slotConfig;
    updateSlots(updatedSlots);
    
    // Save to localStorage
    const savedSettings = JSON.parse(localStorage.getItem('acumen-bot-settings') || '{}');
    savedSettings[activeSlotId] = slotConfig;
    localStorage.setItem('acumen-bot-settings', JSON.stringify(savedSettings));
    
    onClose();
  };

  const handleAgentChange = (index, value) => {
    const newAgents = [...slotConfig.agents];
    newAgents[index] = value;
    setSlotConfig({ ...slotConfig, agents: newAgents });
  };

  const addAgentSlot = () => {
    if (slotConfig.agents.length < 3) {
      setSlotConfig({ ...slotConfig, agents: [...slotConfig.agents, ''] });
    }
  };

  const removeAgentSlot = (index) => {
    const newAgents = slotConfig.agents.filter((_, i) => i !== index);
    setSlotConfig({ ...slotConfig, agents: newAgents });
  };

  const handleDuplicate = () => {
    const newSlotId = `${activeSlotId} 2`;
    const newSlotConfig = { ...slotConfig, name: `${slotConfig.name} 2` };
    
    const updatedSlots = { ...slots, [newSlotId]: newSlotConfig };
    updateSlots(updatedSlots);
    
    // Save to localStorage
    const savedSettings = JSON.parse(localStorage.getItem('acumen-bot-settings') || '{}');
    savedSettings[newSlotId] = newSlotConfig;
    localStorage.setItem('acumen-bot-settings', JSON.stringify(savedSettings));
    
    onClose();
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue;
    setInputValue('');
    
    setMessages(prev => [...prev, { text: userMessage, sender: 'user' }]);
    
    try {
      // Simulate API call to Acumen
      const response = await new Promise(resolve => {
        setTimeout(() => {
          resolve(`I received your message about "${userMessage}". How can I assist you further with ${currentTab} settings?`);
        }, 500);
      });
      
      setMessages(prev => [...prev, { text: response, sender: 'acumen' }]);
    } catch (error) {
      setMessages(prev => [...prev, { text: 'Error processing request', sender: 'acumen' }]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderTabContent = () => {
    const activeTabConfig = tabs.find(tab => tab.id === currentTab);
    
    if (currentTab === 'SIGNATURE') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Officer Name</label>
            <input
              type="text"
              value={slotConfig.signature?.officerName || ''}
              onChange={(e) => setSlotConfig({
                ...slotConfig,
                signature: { ...slotConfig.signature, officerName: e.target.value }
              })}
              className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Agency</label>
            <input
              type="text"
              value={slotConfig.signature?.agency || ''}
              onChange={(e) => setSlotConfig({
                ...slotConfig,
                signature: { ...slotConfig.signature, agency: e.target.value }
              })}
              className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Officer Title/Level</label>
            <input
              type="text"
              value={slotConfig.signature?.title || ''}
              onChange={(e) => setSlotConfig({
                ...slotConfig,
                signature: { ...slotConfig.signature, title: e.target.value }
              })}
              className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Government ID Number</label>
            <input
              type="text"
              value={slotConfig.signature?.idNumber || ''}
              onChange={(e) => setSlotConfig({
                ...slotConfig,
                signature: { ...slotConfig.signature, idNumber: e.target.value }
              })}
              className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Clearance Level</label>
            <select
              value={slotConfig.signature?.clearanceLevel || 'Unclassified'}
              onChange={(e) => setSlotConfig({
                ...slotConfig,
                signature: { ...slotConfig.signature, clearanceLevel: e.target.value }
              })}
              className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            >
              {clearanceLevels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Stamp Style</label>
            <div className="flex space-x-4">
              {stampStyles.map(style => (
                <label key={style} className="flex items-center">
                  <input
                    type="radio"
                    name="stampStyle"
                    checked={slotConfig.signature?.stampStyle === style}
                    onChange={() => setSlotConfig({
                      ...slotConfig,
                      signature: { ...slotConfig.signature, stampStyle: style }
                    })}
                    className="mr-2"
                  />
                  {style}
                </label>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Default Notes Template</label>
            <textarea
              value={slotConfig.signature?.notesTemplate || ''}
              onChange={(e) => setSlotConfig({
                ...slotConfig,
                signature: { ...slotConfig.signature, notesTemplate: e.target.value }
              })}
              rows="4"
              className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            />
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-1">Bot Name</label>
          <input
            type="text"
            value={slotConfig.name || ''}
            onChange={(e) => setSlotConfig({ ...slotConfig, name: e.target.value })}
            className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Color</label>
          <div className="flex space-x-2">
            {presetColors.map(color => (
              <button
                key={color}
                onClick={() => setSlotConfig({ ...slotConfig, color })}
                className={`w-8 h-8 rounded-full border ${slotConfig.color === color ? 'border-gray-800 dark:border-white' : 'border-gray-300 dark:border-gray-600'}`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Cursor Icon</label>
          <div className="grid grid-cols-6 gap-2">
            {cursorIcons.map(icon => (
              <button
                key={icon}
                onClick={() => setSlotConfig({ ...slotConfig, cursorIcon: icon })}
                className={`text-lg p-2 rounded-lg border ${slotConfig.cursorIcon === icon ? 'border-blue-500 bg-blue-100 dark:bg-blue-900' : 'border-gray-300 dark:border-gray-600'}`}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Tone: Formal ↔ Casual</label>
            <input
              type="range"
              min="0"
              max="100"
              value={slotConfig.tone || 50}
              onChange={(e) => setSlotConfig({ ...slotConfig, tone: parseInt(e.target.value) })}
              className="w-full accent-blue-500"
              style={{ accentColor: activeTabConfig.color }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Depth: Quick ↔ Thorough</label>
            <input
              type="range"
              min="0"
              max="100"
              value={slotConfig.depth || 50}
              onChange={(e) => setSlotConfig({ ...slotConfig, depth: parseInt(e.target.value) })}
              className="w-full accent-blue-500"
              style={{ accentColor: activeTabConfig.color }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Speed: Fast ↔ Deep</label>
            <input
              type="range"
              min="0"
              max="100"
              value={slotConfig.speed || 50}
              onChange={(e) => setSlotConfig({ ...slotConfig, speed: parseInt(e.target.value) })}
              className="w-full accent-blue-500"
              style={{ accentColor: activeTabConfig.color }}
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {slotConfig.speed && slotConfig.speed <= 33 ? 'Qwen' : 
               slotConfig.speed && slotConfig.speed >= 67 ? 'R1' : 'Balanced'}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Confidence: Suggest ↔ Assert</label>
            <input
              type="range"
              min="0"
              max="100"
              value={slotConfig.confidence || 50}
              onChange={(e) => setSlotConfig({ ...slotConfig, confidence: parseInt(e.target.value) })}
              className="w-full accent-blue-500"
              style={{ accentColor: activeTabConfig.color }}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Agent Assignments</label>
          <div className="space-y-2">
            {slotConfig.agents?.map((agent, index) => (
              <div key={index} className="flex items-center space-x-2">
                <select
                  value={agent}
                  onChange={(e) => handleAgentChange(index, e.target.value)}
                  className="flex-1 p-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                >
                  <option value="">Select Agent</option>
                  {agentOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <button
                  onClick={() => removeAgentSlot(index)}
                  className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg"
                >
                  ×
                </button>
              </div>
            ))}
            {slotConfig.agents?.length < 3 && (
              <button
                onClick={addAgentSlot}
                className="flex items-center text-blue-500 hover:text-blue-700 dark:hover:text-blue-400"
              >
                + Add Agent
              </button>
            )}
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={handleDuplicate}
            className="flex items-center text-sm px-3 py-1.5 border rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Duplicate Bot
          </button>
          <button
            onClick={handleSave}
            className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
            style={{ backgroundColor: activeTabConfig.color }}
          >
            Save Settings
          </button>
        </div>
      </div>
    );
  };

  return (
    <div 
      className="fixed inset-0 z-10002 bg-black bg-opacity-60 flex items-center justify-center p-4" data-acumen-ui="true"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-[780px] h-[680px] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold">AI Command Bar Settings</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setCurrentTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium relative ${
                currentTab === tab.id 
                  ? 'text-blue-600 dark:text-blue-400' 
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              style={{
                borderBottom: currentTab === tab.id 
                  ? `2px solid ${tab.color}` 
                  : 'none'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Settings Panel - full width */}
          <div className="overflow-y-auto border-b border-gray-200 dark:border-gray-700" style={{height:"420px"}}>
            <BotSettingLeftPanel
              slot={{ id: activeSlotId, name: slotConfig.name || activeSlotId, mode: currentTab.toLowerCase(), agents: slotConfig.agents || [], cursorIcon: slotConfig.cursorIcon, personality: slotConfig.personality }}
              slotColor={tabs.find(t => t.id === currentTab)?.color || '#3b82f6'}
              onSave={(updated) => { setSlotConfig(updated); handleSave(); }}
            />
          </div>

          {/* Chat Panel - below, compact */}
          <div className="flex flex-col border-t border-gray-200 dark:border-gray-700" style={{height:'160px'}}>
            <div className="flex-1 p-2 overflow-y-auto">
              {messages.map((msg, index) => (
                <div key={index} style={{
                  padding: '6px 10px', borderRadius: '8px', fontSize: '12px', marginBottom: '4px',
                  maxWidth: '80%',
                  marginLeft: msg.sender === 'user' ? 'auto' : undefined,
                  backgroundColor: msg.sender === 'user' ? '#3b82f6' : '#f3f4f6',
                  color: msg.sender === 'user' ? 'white' : '#111827'
                }}>
                  {msg.text}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <div className="p-2 border-t border-gray-200 dark:border-gray-700 flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask Acumen about agents, settings..."
                className="flex-1 p-2 text-sm border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
              />
              <button onClick={handleSendMessage} className="px-3 py-1 bg-purple-500 text-white rounded-lg hover:bg-purple-600 text-sm">
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcumenBotSettings;
